// src/components/Dashboard/DetailDialogShell.tsx
//
// The frame both Outsourcing detail dialogs sit in. An inquiry and a
// requisition are read in the same shaped box - full-width xl capped at 95vw,
// a title row carrying the close button, a divided scrolling body - and that
// box was written out twice, so the two already drifted apart on width once
// (the requisition dialog was widened to xl for its item table, the inquiry
// dialog stayed on lg). One shell, one width.
//
// The wide cap is what the item-line tables need: they carry a preview, a
// spec and a files column, and the alternative is a table that scrolls
// sideways inside the dialog.
import { FunctionComponent, ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

type PropsType = {
  open: boolean;
  /** Shown at the left of the title row. A node, because both dialogs
      compose the record's number into it. */
  title: ReactNode;
  onClose: () => void;
  /** While true the body is a placeholder block instead of `children`. */
  isLoading?: boolean;
  /** How tall that placeholder is - roughly what the body will occupy. */
  loadingHeight?: number;
  children: ReactNode;
};

const DetailDialogShell: FunctionComponent<PropsType> = ({
  open,
  title,
  onClose,
  isLoading,
  loadingHeight = 400,
  children,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xl"
    fullWidth
    scroll="paper"
    PaperProps={{ sx: { width: "100%", maxWidth: "min(1800px, 95vw)" } }}
  >
    <DialogTitle
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span>{title}</span>
      <IconButton onClick={onClose} size="small">
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      {isLoading ? (
        <Skeleton variant="rectangular" width="100%" height={loadingHeight} />
      ) : (
        children
      )}
    </DialogContent>
  </Dialog>
);

export default DetailDialogShell;
